import { useId, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import CveLink from '../../components/CveLink';
import Chip, { ChipList } from '../../components/Chip';
import EmptyState from '../../components/EmptyState';
import { DASH, fmtDate, fmtNumber, fmtVersion } from '../../lib/format';
import type { DtCveInstanz, DtCveRow, DtSeverity } from '../../lib/types';
import { SEVERITY_LABELS, SEVERITY_TONE, severityRank } from './severity';

interface ComponentGroup {
  key: string;
  name: string;
  version: string | null;
  severity: DtSeverity | null;
  instances: Map<number, { row: DtCveInstanz; suppressed: boolean }>;
}

/** Group identical component versions without counting repeated findings as instances. */
function groupComponents(row: DtCveRow): ComponentGroup[] {
  const groups = new Map<string, ComponentGroup>();
  const coveredLabels = new Set<string>();
  for (const instance of row.instanzen) {
    for (const component of instance.komponenten) {
      const key = JSON.stringify([component.name, component.version, component.purl]);
      let group = groups.get(key);
      if (!group) {
        group = {
          key, name: component.name, version: component.version,
          severity: component.severity, instances: new Map(),
        };
        groups.set(key, group);
      }
      if (severityRank(component.severity) < severityRank(group.severity)) group.severity = component.severity;
      const previous = group.instances.get(instance.instanz_id);
      group.instances.set(instance.instanz_id, {
        row: instance,
        suppressed: previous ? previous.suppressed && component.suppressed : component.suppressed,
      });
      coveredLabels.add(`${component.name} ${component.version}`.trim());
    }
  }
  // Keep summary-only data visible without guessing a version or instance association.
  for (const label of row.komponenten) {
    if (coveredLabels.has(label.trim())) continue;
    const key = JSON.stringify(['summary', label]);
    groups.set(key, { key, name: label, version: null, severity: null, instances: new Map() });
  }
  return [...groups.values()];
}

function Arrow(): ReactNode {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14m-6-6 6 6-6 6" /></svg>;
}

export default function CveDetail({ row, canReadCmdb }: { row: DtCveRow; canReadCmdb: boolean }): ReactNode {
  const components = useMemo(() => groupComponents(row), [row]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected = components.find((component) => component.key === selectedKey) ?? null;
  const instances = selected ? row.instanzen.filter((instance) => selected.instances.has(instance.instanz_id)) : row.instanzen;
  const instancesId = useId();

  const componentContent = (component: ComponentGroup): ReactNode => {
    const suppressedCount = [...component.instances.values()].filter((instance) => instance.suppressed).length;
    return <>
      <span className="cve-component-heading">
        <span className="cve-component-icon" aria-hidden="true">
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" /><path d="m4 7.5 8 4.5 8-4.5M12 12v9M8 5.3l8 4.5" /></svg>
        </span>
        <strong>{component.name || DASH}</strong>
        {component.severity ? <Chip tone={SEVERITY_TONE[component.severity]}>{SEVERITY_LABELS[component.severity]}</Chip> : null}
      </span>
      <span className="cve-component-field"><span>Version</span><code>{fmtVersion(component.version)}</code></span>
      {suppressedCount > 0 ? <span className="cve-component-suppression">{suppressedCount === component.instances.size ? 'In allen zugeordneten Instanzen unterdrückt' : `In ${fmtNumber(suppressedCount)} von ${fmtNumber(component.instances.size)} Instanzen unterdrückt`}</span> : null}
      <span className="cve-component-footer">
        <span>{component.instances.size > 0 ? `${fmtNumber(component.instances.size)} ${component.instances.size === 1 ? 'betroffene Instanz' : 'betroffene Instanzen'}` : 'Keine Instanzzuordnung verfügbar'}</span>
        {component.instances.size > 0 ? <Arrow /> : null}
      </span>
    </>;
  };

  return (
    <div className="cve-detail">
      <div className="cve-detail-grid">
        <div className="cve-detail-context">
          <section className="cve-advisory" aria-label={`Details zu ${row.cve_id}`}>
            <h3>{row.title || row.cve_id}</h3>
            {row.description ? row.description.length > 320 ? (
              <details className="cve-description">
                <summary>Vollständige Beschreibung lesen</summary><p>{row.description}</p>
              </details>
            ) : <p className="cve-description-text">{row.description}</p> : null}
            <dl className="cve-metadata">
              <div><dt>Quelle</dt><dd>{row.source_label || row.source || DASH}</dd></div>
              <div><dt>Veröffentlicht</dt><dd>{fmtDate(row.published)}</dd></div>
              {row.epss_percentile !== null ? <div><dt>EPSS-Perzentil</dt><dd>{fmtNumber(Math.round(row.epss_percentile * 100))} %</dd></div> : null}
            </dl>
            <div className="cve-advisory-links"><CveLink cve={row.cve_id} tone="neutral" source={row.source} links={row.source_links} /></div>
          </section>

          <section className="cve-components" aria-label="Betroffene Komponenten">
            <div className="cve-affected-heading"><h3>Betroffene Komponenten</h3><Chip>{fmtNumber(components.length)}</Chip></div>
            <p className="cve-component-hint">Komponente auswählen, um die zugehörigen Instanzen zu sehen.</p>
            {components.length > 0 ? <ul className="cve-component-list">
              {components.map((component) => <li key={component.key}>
                {component.instances.size > 0 ? (
                  <button type="button" className="cve-component-card" aria-pressed={selected?.key === component.key} aria-controls={instancesId}
                    onClick={() => setSelectedKey((current) => current === component.key ? null : component.key)}>
                    {componentContent(component)}
                    <span className="visually-hidden">Zugehörige Instanzen anzeigen</span>
                  </button>
                ) : <div className="cve-component-card">{componentContent(component)}</div>}
              </li>)}
            </ul> : <EmptyState compact title="Keine Komponenteninformationen verfügbar" />}
          </section>
        </div>

        <section className="cve-affected" aria-label="Betroffene Instanzen" id={instancesId}>
          <div className="cve-affected-heading"><h3>Betroffene Instanzen</h3><Chip>{fmtNumber(instances.length)}</Chip></div>
          {selected ? <div className="cve-instance-filter" role="status">
            <span>Für <strong>{selected.name}</strong>{selected.version ? ` · ${fmtVersion(selected.version)}` : ''}</span>
            <button type="button" onClick={() => setSelectedKey(null)}>Alle Instanzen anzeigen</button>
          </div> : null}
          <ul className="cve-instance-list">{instances.map((inst) => (
            <li key={inst.instanz_id}>
              <div className="cve-instance-heading">
                {canReadCmdb ? <Link to={`/stammdaten?klasse=ProduktInstanz&id=${inst.instanz_id}`} title="Stammdaten dieser Produktinstanz öffnen"><strong>{inst.produkt || DASH}</strong><Arrow /></Link> : <strong>{inst.produkt || DASH}</strong>}
              </div>
              <dl className="cve-instance-meta">
                <div><dt>Kunde</dt><dd>{inst.kunde || DASH}</dd></div>
                <div><dt>Seriennummer</dt><dd className="mono">{inst.seriennummer || DASH}</dd></div>
                <div className="cve-instance-version"><dt>Version</dt><dd className="mono">{fmtVersion(inst.version, inst.produkt)}</dd></div>
              </dl>
              <ChipList className="cve-instance-components">{inst.komponenten.map((comp, index) => <Chip key={`${comp.name}@${comp.version}@${comp.purl ?? ''}@${index}`} mono
                tone={comp.suppressed ? 'neutral' : SEVERITY_TONE[comp.severity] ?? 'neutral'}
                title={comp.suppressed ? 'In Dependency-Track unterdrückt' : SEVERITY_LABELS[comp.severity]}>
                {comp.name} {fmtVersion(comp.version)}{comp.suppressed ? ' · unterdrückt' : ''}
              </Chip>)}</ChipList>
            </li>
          ))}</ul>
        </section>
      </div>
      <p className="cve-detail-note">Befunde aus Dependency-Track. Die Anwendbarkeit bewerten Sie unter „Triage“.</p>
    </div>
  );
}
